import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../context/AuthContext';
import { useDeadlines } from '../hooks/useDeadlines';
import { usePrintBracket } from '../hooks/usePrintBracket';
import BracketPrintView from '../components/BracketPrintView';
import { resolveAdvancingTeam, useBracketWiring, buildFeeder, getTeamsForGame } from '../utils/bracketLogic';

const DEFAULT_ROUND_POINTS = { playin: 1, R64: 1, R32: 2, R16: 3, R8: 4, R4: 5, Final: 10 };
const REGIONS = ['South', 'East', 'Midwest', 'West'];

// Games within each region, in bracket order (top half then bottom half)
// Each array is [round, gameIdSuffix] pairs for that region
const REGION_STRUCTURE = {
  South: {
    R64:  ['r64-s1','r64-s2','r64-s3','r64-s4','r64-s5','r64-s6','r64-s7','r64-s8'],
    R32:  ['r32-s1','r32-s2','r32-s3','r32-s4'],
    R16:  ['r16-s1','r16-s2'],
    R8:   ['r8-s'],
  },
  East: {
    R64:  ['r64-e1','r64-e2','r64-e3','r64-e4','r64-e5','r64-e6','r64-e7','r64-e8'],
    R32:  ['r32-e1','r32-e2','r32-e3','r32-e4'],
    R16:  ['r16-e1','r16-e2'],
    R8:   ['r8-e'],
  },
  Midwest: {
    R64:  ['r64-m1','r64-m2','r64-m3','r64-m4','r64-m5','r64-m6','r64-m7','r64-m8'],
    R32:  ['r32-m1','r32-m2','r32-m3','r32-m4'],
    R16:  ['r16-m1','r16-m2'],
    R8:   ['r8-m'],
  },
  West: {
    R64:  ['r64-w1','r64-w2','r64-w3','r64-w4','r64-w5','r64-w6','r64-w7','r64-w8'],
    R32:  ['r32-w1','r32-w2','r32-w3','r32-w4'],
    R16:  ['r16-w1','r16-w2'],
    R8:   ['r8-w'],
  },
};

const REGION_ROUNDS = ['R64','R32','R16','R8'];
const ROUND_LABELS_SHORT = { R64: 'R64', R32: 'R32', R16: 'S16', R8: 'E8' };
const TBD = { id: 'tbd', name: 'TBD', abbrev: 'TBD', seed: 99, isTbd: true };

// Bracket wiring loaded from Firestore via useBracketWiring()

// Build a selectable play-in placeholder from a play-in game doc
function buildPlayInPlaceholder(pg, teamsById) {
  if (!pg) return null;
  const getTeam = slot => {
    if (!slot) return null;
    if (slot.id && slot.id !== 'tbd') return teamsById[slot.id] || slot;
    return slot;
  };
  const t1 = getTeam(pg.homeTeam);
  const t2 = getTeam(pg.awayTeam);
  const PLAY_IN_SUFFIXES = [
    'Tar Heels','Crimson Tide','Blue Devils','Red Raiders','Horned Frogs',
    'Wolf Pack','Golden Eagles','Mean Green','Green Wave','Red Storm',
    'Blue Jays','Demon Deacons','Fighting Illini','Fighting Irish',
    'Sun Devils','Yellow Jackets','Scarlet Knights','Running Rebels',
    'Red Flash','Aztecs','Spartans','Bruins','Bulldogs','Tigers','Bears',
    'Wildcats','Gators','Hoosiers','Buckeyes','Hurricanes','Volunteers',
    'Longhorns','Musketeers','Mountaineers','Eagles','Hawks','Owls',
    'Panthers','Antelopes','Colonials','Hornets','Aggies','Cougars',
  ];
  const abbrev = t => {
    if (!t) return '?';
    const name = t.name || '';
    if (name.length <= 10) return name;
    const lower = name.toLowerCase();
    for (const s of PLAY_IN_SUFFIXES) {
      if (lower.endsWith(s.toLowerCase())) {
        const stripped = name.slice(0, name.length - s.length).trim();
        if (stripped) return stripped.length <= 14 ? stripped : stripped.split(' ')[0].substring(0,1) + '. ' + stripped.split(' ').slice(1).join(' ');
      }
    }
    // Fallback: strip last word
    const words = name.split(' ');
    const school = words.length > 1 ? words.slice(0, -1).join(' ') : name;
    return school.length <= 14 ? school : school.split(' ')[0].substring(0,1) + '. ' + school.split(' ').slice(1).join(' ');
  };
  // Use the team seeds (not 0) — fall back to the dest seed or a '?' display
  const seed1 = t1?.seed || pg.homeTeam?.seed;
  const seed2 = t2?.seed || pg.awayTeam?.seed;
  // Display as "16MSB / 16GT" or just "MSB / GT" if seeds match
  const label = (t1 && t2)
    ? (seed1 === seed2
        ? `${abbrev(t1)} / ${abbrev(t2)}`         // same seed: "MSB / GT"
        : `(${seed1}) ${abbrev(t1)} / (${seed2}) ${abbrev(t2)}`)
    : 'Play-In';
  // Show the destination seed (what slot this winner fills in R64)
  const destSeed = seed1 || seed2 || 16;
  return {
    ...TBD,
    id: 'playin-pending-' + pg.id,
    name: label,
    seed: destSeed,
    isPlayInPending: true,
    playInGameId: pg.id,
  };
}

function resolveTeam(gameId, gamesById, allPicks, teamsById) {
  const game = gamesById[gameId];
  if (!game) return null;

  // Actual result takes priority
  if (game.status === 'final' && game.winner) {
    return teamsById[game.winner]
      || (game.homeTeam?.id === game.winner ? game.homeTeam : game.awayTeam)
      || null;
  }

  const p = allPicks[gameId];
  if (!p) return null;

  // Strip any accidental 'playin-pending-' prefix (shouldn't be stored but be safe)
  const resolvedP = p.startsWith?.('playin-pending-') ? p.replace('playin-pending-', '') : p;

  // Direct team lookup
  const directTeam = teamsById[resolvedP]
    || (game.homeTeam?.id === resolvedP ? game.homeTeam : null)
    || (game.awayTeam?.id === resolvedP ? game.awayTeam : null);
  if (directTeam && !directTeam.isPlayInPending && directTeam.id !== 'tbd') return directTeam;

  // p is a play-in game ID — resolve via the play-in game
  const pg = gamesById[resolvedP];
  if (!pg) {
    // Game not in gamesById yet — return a minimal placeholder so slot stays selectable
    return { ...TBD, id: 'playin-pending-' + resolvedP, name: 'Play-In', seed: 16, isPlayInPending: true, playInGameId: resolvedP };
  }

  // Play-in game has a winner — return the real team
  if (pg.status === 'final' && pg.winner) {
    return teamsById[pg.winner]
      || (pg.homeTeam?.id === pg.winner ? pg.homeTeam : pg.awayTeam)
      || null;
  }

  // Play-in not resolved — return named placeholder so it stays selectable
  return buildPlayInPlaceholder(pg, teamsById);
}

function getSlots(gameId, gamesById, allPicks, teamsById, feeder) {
  const game = gamesById[gameId];
  if (!game) return [TBD, TBD];

  let t1, t2;
  const _feeder = feeder || {};
  if (_feeder[gameId]) {
    const [f1, f2] = _feeder[gameId];
    // f1 = top bracket position, f2 = bottom — preserve this order always
    t1 = resolveTeam(f1, gamesById, allPicks, teamsById) || TBD;
    t2 = resolveTeam(f2, gamesById, allPicks, teamsById) || TBD;
  } else {
    // R64: homeTeam = top of bracket, awayTeam = bottom
    // Handle Play-In slots (playInGameId reference)
    const getR64Team = (slotTeam) => {
      if (!slotTeam) return TBD;
      if (slotTeam.playInGameId) {
        const pg = gamesById[slotTeam.playInGameId];
        // If game is final, use actual winner
        if (pg?.status === 'final' && pg.winner) {
          return teamsById[pg.winner] || (pg.homeTeam?.id === pg.winner ? pg.homeTeam : pg.awayTeam) || TBD;
        }
        // If user has a pick for this play-in game, show that team
        const pick = allPicks[slotTeam.playInGameId];
        if (pick) return teamsById[pick] || TBD;
        // No pick yet — build a selectable "Team A / Team B" placeholder
        if (pg) return buildPlayInPlaceholder(pg, teamsById);
        return { ...TBD, name: slotTeam.name || 'Play-In', seed: slotTeam.seed, isPlayInPending: true, playInGameId: slotTeam.playInGameId };
      }
      if (slotTeam.id === 'tbd') return TBD;
      return teamsById[slotTeam.id] || slotTeam;
    };
    t1 = getR64Team(game.homeTeam);
    t2 = getR64Team(game.awayTeam);
  }

  // Never re-sort — bracket position is determined by which game slot they came from
  return [t1, t2];
}

// ── Main component ───────────────────────────────────────────────────────────
export default function BracketPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const api = useApi();
  const isNew = !id || id === 'new';
  const { wiring, feeder, regionOrder, overallSeedByRegion, loading: wiringLoading } = useBracketWiring();
  const { userProfile } = useAuth();
  const { pastPlayIn, pastRegular, playInLabel, regularLabel, showRegularAlert, showPlayInAlert } = useDeadlines();
  const { printData, printLoading, printError, openPrint, closePrint } = usePrintBracket();

  const [games, setGames] = useState([]);
  const [picks, setPicks] = useState({});
  const [playInPicks, setPlayInPicks] = useState({});
  const [bracketName, setBracketName] = useState('');
  const [sleeperTeamId, setSleeperTeamId] = useState('');
  const [sleeperTeams, setSleeperTeams] = useState([]);
  const [teamsById, setTeamsById] = useState({});
  const [loading, setLoading] = useState(true);
  const [roundPoints, setRoundPoints] = useState(DEFAULT_ROUND_POINTS);
  const [sleeperMode, setSleeperMode] = useState('match_round');
  const [sleeperCustomPts, setSleeperCustomPts] = useState(0);
  const [playInRoundName, setPlayInRoundName] = useState('First Four');
  const [tiebreaker, setTiebreaker] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [cloneBrackets, setCloneBrackets] = useState([]);
  const [cloneLoading, setCloneLoading] = useState(false);
  const [activeView, setActiveView] = useState('__loading__'); // placeholder until wiring known

  useEffect(() => {
    async function load() {
      try {
        const withTimeout = (promise, fallback, ms = 10000) =>
          Promise.race([promise, new Promise(r => setTimeout(() => r(fallback), ms))]);

        const [gamesData, sleeperData, teamsData] = await Promise.all([
          withTimeout(api.getScores(), []),
          withTimeout(api.getSleeperTeams(), { teams: [] }),
          withTimeout(api.getTeams(), { teams: [] }),
        ]);

        setGames(Array.isArray(gamesData) ? gamesData : []);
        setSleeperTeams(sleeperData?.teams || []);
        const tById = {};
        (teamsData?.teams || []).forEach(t => { tById[t.id] = t; });
        setTeamsById(tById);

        // Fetch scoring config independently — failure here must never block bracket loading
        fetch(`${process.env.REACT_APP_API_URL || '/api'}/scoring`)
          .then(r => r.ok ? r.json() : null)
          .then(sc => {
            if (sc?.roundPoints) {
              setRoundPoints({ ...DEFAULT_ROUND_POINTS, ...sc.roundPoints });
              if (sc.sleeperMode) setSleeperMode(sc.sleeperMode);
              if (sc.sleeperCustomPts != null) setSleeperCustomPts(sc.sleeperCustomPts);
            }
          })
          .catch(() => {});

        // Fetch app config for playInRoundName
        fetch(`${process.env.REACT_APP_API_URL || '/api'}/scores/config`)
          .then(r => r.ok ? r.json() : null)
          .then(cfg => { if (cfg?.playInRoundName) setPlayInRoundName(cfg.playInRoundName); })
          .catch(() => {});

        // activeView will be set by the wiring useEffect once regionOrder is known
        if (!isNew) {
          const b = await withTimeout(api.getBracket(id), null, 10000);
          if (!b) throw new Error('Bracket load timed out — please try again.');
          setBracketName(b.name);
          setPicks(b.picks || {});
          setPlayInPicks(b.playInPicks || {});
          setSleeperTeamId(b.sleeperTeamId || '');
          setTiebreaker(b.tiebreaker !== null && b.tiebreaker !== undefined ? String(b.tiebreaker) : '');
        }
      } catch (err) { setError(err.message); }
      finally { setLoading(false); }
    }
    load();
  }, [id, pastPlayIn]);

  // Set activeView once when wiring is loaded
  useEffect(() => {
    if (wiringLoading) return;
    setActiveView(prev => {
      if (prev !== '__loading__') return prev; // only set once
      if (!pastPlayIn) return 'playin';
      return regionOrder[0] || 'South';
    });
  }, [wiringLoading, regionOrder, pastPlayIn]);

  const allPicks = useMemo(() => ({ ...picks, ...playInPicks }), [picks, playInPicks]);
  const gamesById = useMemo(() => {
    const m = {}; games.forEach(g => { m[g.id] = g; }); return m;
  }, [games]);

  // Build set of eliminated pick IDs (works with ESPN numeric IDs)
  const eliminatedPickIds = useMemo(() => {
    const eliminated = new Set();
    const allP = { ...picks, ...playInPicks };
    // For each final game, the loser's id AND any pick that != winner
    games.forEach(g => {
      if ((g.status === 'final' || g.status === 'Final') && g.winner) {
        // Add loser's team id
        const home = g.homeTeam, away = g.awayTeam;
        if (home && away) {
          const loser = String(home.id) === String(g.winner) ? away : home;
          if (loser?.id && loser.id !== 'tbd') eliminated.add(String(loser.id));
        }
        // Add the pick id if it != winner
        const pid = allP[g.id];
        if (pid && String(pid) !== String(g.winner)) eliminated.add(String(pid));
      }
      // Play-in picks
      if (g.round === 'playin' && g.winner) {
        const pid = allP[g.id];
        if (pid && String(pid) !== String(g.winner)) eliminated.add(String(pid));
      }
    });
    return eliminated;
  }, [games, picks, playInPicks]);

  function pick(gameId, teamId, isPlayin) {
    if (isPlayin) {
      setPlayInPicks(p => {
        const updated = { ...p };
        if (p[gameId] === teamId) delete updated[gameId]; // toggle off
        else updated[gameId] = teamId;
        return updated;
      });
      return;
    }

    // Resolve the actual stored teamId (strip playin-pending prefix if needed)
    const storedId = (teamId && teamId.startsWith('playin-pending-'))
      ? teamId.replace('playin-pending-', '')
      : teamId;

    setPicks(currentPicks => {
      const oldPick = currentPicks[gameId];

      // Clicking the already-picked team toggles it off — then cascade-clear downstream
      const storedIdFinal = (oldPick === storedId) ? undefined : storedId;
      const newPicks = { ...currentPicks };
      if (storedIdFinal === undefined) delete newPicks[gameId];
      else newPicks[gameId] = storedIdFinal;

      // If nothing changed and it wasn't a deselect, nothing to cascade
      if (oldPick === storedIdFinal && storedIdFinal !== undefined) return newPicks;

      // Build reverse feeder: sourceGameId -> downstreamGameId
      const downstream = {};
      Object.entries(feeder || {}).forEach(([downId, sources]) => {
        if (Array.isArray(sources)) {
          sources.forEach(src => { downstream[src] = downId; });
        }
      });

      // Walk forward from gameId, clearing any picks that carried the old team
      const toClear = [gameId];
      while (toClear.length) {
        const current = toClear.pop();
        const next = downstream[current];
        if (!next) continue;
        const downPick = newPicks[next];
        if (downPick && downPick === oldPick) {
          delete newPicks[next];
          toClear.push(next);
        }
      }

      return newPicks;
    });
  }

  function pickResult(gameId, teamId) {
    const g = gamesById[gameId];
    if (g?.winner) {
      return String(teamId) === String(g.winner) ? 'correct' : 'incorrect';
    }
    // Game not played yet — check if this pick is already eliminated
    if (teamId && eliminatedPickIds.has(String(teamId))) return 'eliminated';
    return null;
  }

  // Check if a region is fully picked (all 15 games)
  function regionComplete(region) {
    if (!region || !REGION_STRUCTURE[region]) return false;
    const struct = REGION_STRUCTURE[region];
    return REGION_ROUNDS.every(round =>
      (struct[round] || []).every(gid => !!picks[gid])
    );
  }

  // Count picks for display
  function regionPickCount(region) {
    if (!region || !REGION_STRUCTURE[region]) return { picked: 0, total: 15 };
    const struct = REGION_STRUCTURE[region];
    let picked = 0, total = 0;
    REGION_ROUNDS.forEach(round => {
      (struct[round] || []).forEach(gid => {
        total++;
        if (picks[gid]) picked++;
      });
    });
    return { picked, total };
  }

  async function handleSave() {
    if (!bracketName.trim()) { setError('Please name your bracket'); return; }
    if (!sleeperTeamId) { setError('Please select your Sleeper team'); return; }
    if (tiebreaker === '' || tiebreaker === null || tiebreaker === undefined) {
      setError('Please enter a tiebreaker score');
      return;
    }

    // Validate play-in picks if deadline hasn't passed
    if (!pastPlayIn) {
      const playInGames = games.filter(g => g.round === 'playin');
      const missingPlayIn = playInGames.some(g => g.status === 'scheduled' && !playInPicks[g.id]);
      if (missingPlayIn) {
        setError('Please make all First Four picks before submitting.');
        setActiveView('playin');
        return;
      }
    }

    // Validate all region picks
    const activeRegions = regionOrder.length === 4 ? regionOrder : REGIONS;
    const incompleteRegion = activeRegions.find(r => !regionComplete(r));
    if (incompleteRegion) {
      setError(`Your ${incompleteRegion} region is incomplete — please finish all picks.`);
      setActiveView(incompleteRegion);
      return;
    }

    // Validate Final Four + Championship
    const finalGames = ['r4-1', 'r4-2', 'final-1'];
    const missingFinal = finalGames.find(gid => !picks[gid]);
    if (missingFinal) {
      setError('Please complete your Final Four and Championship picks.');
      setActiveView('finals');
      return;
    }

    setSaving(true); setError('');
    try {
      const data = {
        name: bracketName,
        picks,
        // Don't send playInPicks if past the deadline — backend would reject it
        ...(!pastPlayIn ? { playInPicks } : {}),
        sleeperTeamId,
        tiebreaker: tiebreaker !== '' ? Number(tiebreaker) : null,
      };
      if (isNew) {
        const c = await api.createBracket(data);
        setSuccess('Bracket saved!');
        setTimeout(() => navigate('/'), 1200);
      } else {
        await api.updateBracket(id, data);
        setSuccess('Bracket updated!');
        setTimeout(() => navigate('/'), 1200);
      }
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  }

  async function handleCloneOpen() {
    setCloneLoading(true);
    setShowCloneModal(true);
    try {
      // Fetch brackets and all teams in parallel — build a fresh string-keyed lookup
      // so sleeper names resolve regardless of whether IDs are strings or numbers.
      const [bracketsData, teamsData] = await Promise.all([
        api.getBrackets(),
        api.getTeams().catch(() => ({ teams: [] })),
      ]);
      const brackets = Array.isArray(bracketsData) ? bracketsData : (bracketsData?.brackets || []);
      const allTeamsById = {};
      (teamsData?.teams || []).forEach(t => { allTeamsById[String(t.id)] = t; });
      // Fold in already-loaded state as additional fallbacks
      Object.entries(teamsById).forEach(([k, v]) => { if (!allTeamsById[k]) allTeamsById[k] = v; });
      sleeperTeams.forEach(t => { if (!allTeamsById[String(t.id)]) allTeamsById[String(t.id)] = t; });
      // Attach resolved sleeper name to each bracket for display
      const enriched = brackets.map(b => ({
        ...b,
        _sleeperName: b.sleeperTeamId ? (allTeamsById[String(b.sleeperTeamId)]?.name || null) : null,
      }));
      setCloneBrackets(enriched);
    } catch (err) {
      setError('Failed to load your brackets: ' + err.message);
      setShowCloneModal(false);
    } finally {
      setCloneLoading(false);
    }
  }

  function handleCloneSelect(source) {
    // Copy picks, sleeper — drop playInPicks if past deadline
    setPicks(source.picks || {});
    setPlayInPicks(pastPlayIn ? {} : (source.playInPicks || {}));
    // Only copy sleeper if it resolved to a known name — otherwise clear it so
    // the user is forced to pick a valid one rather than silently inheriting a bad ID.
    const sid = source._sleeperName && source.sleeperTeamId != null
      ? String(source.sleeperTeamId)
      : '';
    setSleeperTeamId(sid);
    setTiebreaker(source.tiebreaker !== null && source.tiebreaker !== undefined ? String(source.tiebreaker) : '');
    setBracketName(''); // user must give the clone a new name
    setShowCloneModal(false);
    setError('');
    // Jump to first region so they can review picks
    setActiveView(regionOrder[0] || 'South');
  }

  if (loading) return <div className="loading-container"><div className="spinner"></div><div className="loading-text">Loading bracket...</div></div>;

  // Block new bracket creation if past regular deadline
  if (isNew && pastRegular) {
    return (
      <div>
        <div className="page-header">
          <h1 className="page-title">NEW <span>BRACKET</span></h1>
        </div>
        <div className="card" style={{ textAlign: 'center', padding: '48px 32px' }}>
          <div style={{ fontSize: '3rem', marginBottom: 16 }}>🔒</div>
          <h2 style={{ fontFamily: 'var(--font-display)', color: 'var(--cream)', marginBottom: 12 }}>Submissions Closed</h2>
          <p style={{ color: 'var(--gray)', fontFamily: 'var(--font-condensed)', fontSize: '1.1rem', marginBottom: 8 }}>
            The bracket submission deadline has passed.
          </p>
          {regularLabel && (
            <p style={{ color: 'var(--gray)', fontSize: '0.9rem', marginBottom: 24 }}>Deadline was: {regularLabel}</p>
          )}
          <button className="btn btn-secondary" onClick={() => navigate('/')}>← Back to Dashboard</button>
        </div>
      </div>
    );
  }

  const activeRegions = regionOrder.length === 4 ? regionOrder : REGIONS;
  const views = ['playin', ...activeRegions, 'finals'];

  // effectiveView: '__loading__' means wiring not ready yet
  // For new brackets past play-in deadline: always show first real region, never 'playin'
  const effectiveView = (activeView === '__loading__')
    ? activeRegions[0]  // safe fallback while wiring loads (will correct via effect)
    : (isNew && pastPlayIn && activeView === 'playin')
      ? activeRegions[0]
      : activeView;
  const playInGames = games.filter(g => g.round === 'playin');
  const playInPicked = playInGames.filter(g => playInPicks[g.id]).length;

  // Finals games
  const finalsGames = [
    gamesById['r4-1'], gamesById['r4-2'], gamesById['final-1'],
  ].filter(Boolean);
  const finalsPicked = finalsGames.filter(g => picks[g.id]).length;

  // readOnly: existing bracket past regular deadline — view only, no changes allowed
  const readOnly = !isNew && pastRegular;

  return (
    <div>
      {/* Header */}
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 16 }}>
        <div>
          <h1 className="page-title">
            {readOnly ? 'VIEW' : isNew ? 'NEW' : 'EDIT'} <span>BRACKET</span>
          </h1>
          {readOnly && <p className="page-subtitle" style={{ color: 'var(--gray)' }}>🔒 Bracket is locked — viewing picks only</p>}
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <button className="btn btn-secondary" onClick={() => navigate('/')}>← Back</button>
          {!isNew && (
            <button className="btn btn-secondary" onClick={() => openPrint(id, { id, name: bracketName, userName: userProfile?.displayName || '', picks, playInPicks, sleeperTeamId, tiebreaker: tiebreaker !== '' ? Number(tiebreaker) : null })} title="Print / Save PDF">
              🖨 Print / Save PDF
            </button>
          )}
          {!readOnly && (
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : isNew ? '🏆 Submit Bracket' : '💾 Save Changes'}
            </button>
          )}
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {/* Deadline banners */}
      {showRegularAlert && !isNew && (
        <div className="alert alert-error" style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
          <span>🔒</span>
          <span>
            <strong>Bracket submissions are closed{regularLabel ? ` (${regularLabel})` : ''}.</strong>
            {' '}Your picks are locked — no changes can be made.
          </span>
        </div>
      )}
      {showPlayInAlert && (
        <div className="alert alert-error" style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
          <span>⏰</span>
          <span>
            <strong>{playInRoundName} deadline has passed{playInLabel ? ` (${playInLabel})` : ''}.</strong>
            {' '}{playInRoundName} picks are locked. Play-In winners will be automatically filled in as games complete.
          </span>
        </div>
      )}

      {/* Setup Card */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="grid-2" style={{ gap: 20 }}>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Bracket Name</label>
            <input className="form-input" type="text" value={bracketName}
              onChange={e => !readOnly && setBracketName(e.target.value)} placeholder="e.g. My Bold Picks" maxLength={50}
              disabled={readOnly} style={readOnly ? { opacity: 0.6 } : {}} />
            {isNew && (
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                style={{ marginTop: 8 }}
                onClick={handleCloneOpen}
              >
                📋 Clone an Existing Entry
              </button>
            )}
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">🌟 Sleeper Team (Seed 6–16)</label>
            {(() => {
              const eliminatedIds = new Set();
              games.filter(g => g.round === 'playin').forEach(g => {
                if (!g.winner) return;
                const homeId = String(g.homeTeam?.id || '');
                const awayId = String(g.awayTeam?.id || '');
                if (!homeId || !awayId || homeId === 'tbd' || awayId === 'tbd') return;
                const loserId = String(g.winner) === homeId ? awayId : homeId;
                if (loserId) eliminatedIds.add(loserId);
              });
              const availableSleeperTeams = [...sleeperTeams]
                .filter(t => !eliminatedIds.has(String(t.id)))
                .sort((a, b) => a.seed - b.seed || (a.region||'').localeCompare(b.region||''));
              const eliminatedCount = sleeperTeams.length - availableSleeperTeams.length;
              return (
                <>
                  <select className="form-select" value={sleeperTeamId} onChange={e => !readOnly && isNew && setSleeperTeamId(e.target.value)} disabled={readOnly || !isNew} style={(readOnly || !isNew) ? { opacity: 0.6 } : {}}>
                    <option value="">Select your Sleeper...</option>
                    {availableSleeperTeams.map(t => <option key={t.id} value={t.id}>({t.seed}) {t.name} — {t.region}</option>)}
                  </select>
                  {eliminatedCount > 0 && isNew && (
                    <div style={{ fontSize: '0.75rem', color: 'var(--gray)', marginTop: 3 }}>
                      {eliminatedCount} eliminated team{eliminatedCount !== 1 ? 's' : ''} removed from list
                    </div>
                  )}
                  <div style={{ fontSize: '0.8rem', marginTop: 4 }}>
                    {isNew ? (
                      <>
                        <span style={{ color: 'var(--gray)' }}>Bonus pts every time your Sleeper wins. </span>
                        <span style={{ color: 'var(--gold)', fontWeight: 700 }}>⚠ Sleeper cannot be modified once your bracket is submitted!</span>
                      </>
                    ) : (
                      <span style={{ color: 'var(--gray)' }}>🔒 Sleeper selection is final — contact an admin to change.</span>
                    )}
                  </div>
                </>
              );
            })()}
          </div>
        </div>
        <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--slate)', display: 'flex', alignItems: 'center', gap: 20 }}>
          <div style={{ flex: '0 0 auto' }}>
            <label className="form-label">🎯 Tiebreaker — Combined Championship Score</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <input
                className="form-input"
                type="number"
                min="0"
                max="500"
                value={tiebreaker}
                onChange={e => !readOnly && setTiebreaker(e.target.value)}
                placeholder="e.g. 128"
                disabled={readOnly}
                style={{ width: 120, ...(readOnly ? { opacity: 0.6 } : {}) }}
              />
              <span style={{ color: 'var(--gray)', fontSize: '0.85rem' }}>total combined points</span>
            </div>
          </div>
          <div style={{ flex: 1, fontSize: '0.8rem', color: 'var(--gray)', lineHeight: 1.5 }}>
            Closest without going over wins the tiebreaker. If all tied entries are over, closest wins.
          </div>
        </div>
      </div>

      {/* Top Nav */}
      <div style={{ display: 'flex', background: 'var(--navy-mid)', border: '1px solid var(--slate)', borderRadius: '10px', overflow: 'hidden', marginBottom: 20 }}>
        {/* Play-In — hidden if no play-in games OR past deadline and new bracket */}
        {(playInGames.length > 0) && !(isNew && pastPlayIn) && (
          <NavBtn
            label={playInRoundName}
            sub={isNew && pastPlayIn ? '🔒 N/A' : pastPlayIn ? '🔒 View' : `${playInPicked}/4`}
            active={effectiveView === 'playin'}
            done={!pastPlayIn && playInPicked === 4}
            locked={isNew && pastPlayIn}
            onClick={() => setActiveView('playin')}
          />
        )}
        {/* Regions */}
        {activeRegions.map(r => {
          const { picked, total } = regionPickCount(r);
          return <NavBtn key={r} label={readOnly ? `🔒 ${r}` : r} sub={`${picked}/${total}`} active={effectiveView === r}
            done={!readOnly && regionComplete(r)} onClick={() => setActiveView(r)} />;
        })}
        {/* Finals */}
        <NavBtn label={readOnly ? '🔒 Final Four + Champ' : 'Final Four + Champ'} sub={`${finalsPicked}/3`} active={effectiveView === 'finals'}
          done={!readOnly && finalsPicked === 3} onClick={() => setActiveView('finals')} />
      </div>

      {/* ── PLAY-IN VIEW ─────────────────────────────────────────── */}
      {effectiveView === 'playin' && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '2rem', color: 'var(--cream)', letterSpacing: '2px' }}>
              FIRST <span style={{ color: 'var(--orange)' }}>FOUR</span>
            </h2>
            {!pastPlayIn && <><span className="badge badge-orange">{roundPoints.playin} pt{roundPoints.playin !== 1 ? 's' : ''} each</span><span className="badge badge-gold">+1 bonus if ALL 4 correct</span></>}
            {pastPlayIn && <span className="badge badge-gray" style={{ background: 'rgba(231,76,60,0.15)', color: 'var(--red)', border: '1px solid var(--red)' }}>🔒 DEADLINE PASSED</span>}
          </div>

          {pastPlayIn ? (
            // Locked view — show results/pending for each play-in game
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
              {[...playInGames].sort((a,b) => new Date(a.date)-new Date(b.date)).map(game => {
                const winner = game.status === 'final' && game.winner
                  ? (teamsById[game.winner] || (game.homeTeam?.id === game.winner ? game.homeTeam : game.awayTeam))
                  : null;
                const myPick = playInPicks[game.id] ? teamsById[playInPicks[game.id]] : null;
                return (
                  <div key={game.id} style={{ border: '1px solid var(--slate)', borderRadius: 10, overflow: 'hidden', background: 'var(--navy-mid)', opacity: 0.85 }}>
                    <div style={{ padding: '6px 10px', background: 'var(--navy-light)', display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid var(--slate)' }}>
                      <span style={{ fontSize: '0.7rem', color: 'var(--gray)', fontFamily: 'var(--font-condensed)', letterSpacing: '1px', textTransform: 'uppercase' }}>{game.shortName}</span>
                      <span>
                        {game.status === 'final'
                          ? <span className="badge badge-gray" style={{ fontSize: '0.65rem' }}>FINAL</span>
                          : <span className="badge badge-orange" style={{ fontSize: '0.65rem' }}>PENDING</span>}
                      </span>
                    </div>
                    <div style={{ padding: '10px 12px' }}>
                      {winner ? (
                        <div>
                          <div style={{ fontSize: '0.75rem', color: 'var(--gray)', marginBottom: 4 }}>Winner:</div>
                          <div style={{ fontFamily: 'var(--font-condensed)', fontWeight: 700, color: 'var(--green)', fontSize: '1rem' }}>
                            ({winner.seed}) {winner.name}
                          </div>
                          {myPick && (
                            <div style={{ marginTop: 6, fontSize: '0.8rem', color: myPick.id === game.winner ? 'var(--green)' : 'var(--red)' }}>
                              Your pick: ({myPick.seed}) {myPick.name} {myPick.id === game.winner ? '✓' : '✗'}
                            </div>
                          )}
                          {!myPick && <div style={{ marginTop: 6, fontSize: '0.8rem', color: 'var(--gray)', fontStyle: 'italic' }}>No pick submitted</div>}
                        </div>
                      ) : (
                        <div>
                          <div style={{ fontFamily: 'var(--font-condensed)', color: 'var(--gray)', fontStyle: 'italic' }}>
                            Game not yet played — winner goes to R64
                          </div>
                          {myPick && (
                            <div style={{ marginTop: 6, fontSize: '0.8rem', color: 'var(--orange)' }}>
                              Your pick: ({myPick.seed}) {myPick.name}
                            </div>
                          )}
                          {!myPick && <div style={{ marginTop: 6, fontSize: '0.8rem', color: 'var(--gray)', fontStyle: 'italic' }}>No pick submitted</div>}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            // Open for picks
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
              {[...playInGames].sort((a,b) => new Date(a.date)-new Date(b.date)).map(game => (
                <MiniGameCard key={game.id} game={game}
                  topTeam={game.homeTeam?.seed <= game.awayTeam?.seed ? game.homeTeam : game.awayTeam}
                  bottomTeam={game.homeTeam?.seed <= game.awayTeam?.seed ? game.awayTeam : game.homeTeam}
                  currentPick={playInPicks[game.id]} sleeperTeamId={sleeperTeamId}
                  onPick={tid => pick(game.id, tid, true)} pickResult={pickResult} />
              ))}
            </div>
          )}

          <BottomNav
            onNext={() => setActiveView(activeRegions[0])}
            nextLabel={`${activeRegions[0]} Region →`}
            prevLabel={null}
          />
        </div>
      )}

      {/* ── REGION VIEW ──────────────────────────────────────────── */}
      {activeRegions.includes(effectiveView) && (
        <RegionBracket
          region={effectiveView}
          gamesById={gamesById}
          picks={picks}
          allPicks={allPicks}
          teamsById={teamsById}
          sleeperTeamId={sleeperTeamId}
          onPick={(gid, tid) => !readOnly && pick(gid, tid, false)}
          pickResult={pickResult}
          readOnly={readOnly}
          onPrev={() => {
            const idx = activeRegions.indexOf(effectiveView);
            if (idx === 0) setActiveView(pastPlayIn ? 'playin' : 'playin');
            else setActiveView(activeRegions[idx - 1]);
          }}
          onNext={() => {
            const idx = activeRegions.indexOf(effectiveView);
            setActiveView(idx === activeRegions.length - 1 ? 'finals' : activeRegions[idx + 1]);
          }}
          prevLabel={activeRegions.indexOf(effectiveView) === 0
            ? (pastPlayIn ? null : pastPlayIn ? null : `← ${playInRoundName}`)
            : `← ${activeRegions[activeRegions.indexOf(effectiveView) - 1]}`}
          nextLabel={activeRegions.indexOf(effectiveView) === activeRegions.length - 1
            ? 'Final Four →'
            : `${activeRegions[activeRegions.indexOf(effectiveView) + 1]} →`}
          feeder={feeder}
          overallSeedByRegion={overallSeedByRegion}
          roundPoints={roundPoints}
        />
      )}

      {/* ── FINALS VIEW ──────────────────────────────────────────── */}
      {effectiveView === 'finals' && (
        <FinalsView
          gamesById={gamesById} picks={picks} allPicks={allPicks}
          teamsById={teamsById} sleeperTeamId={sleeperTeamId}
          onPick={(gid, tid) => !readOnly && pick(gid, tid, false)}
          pickResult={pickResult}
          readOnly={readOnly}
          onPrev={() => setActiveView(activeRegions[activeRegions.length - 1])}
          prevRegionLabel={activeRegions[activeRegions.length - 1]}
          wiring={wiring}
          feeder={feeder}
          roundPoints={roundPoints}
          onSave={handleSave} saving={saving} isNew={isNew}
        />
      )}
      {printLoading && (
        <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:999,display:'flex',alignItems:'center',justifyContent:'center' }}>
          <div className="spinner" />
        </div>
      )}
      {printData && (
        <BracketPrintView
          bracket={printData.bracket}
          games={printData.games}
          teamsById={printData.teamsById}
          wiring={printData.wiring}
          branding={printData.branding}
          playInRoundName={playInRoundName}
          onClose={closePrint}
        />
      )}

      {/* Clone Modal */}
      {showCloneModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: 'var(--navy)', border: '1px solid var(--slate)', borderRadius: 12, width: '100%', maxWidth: 480, maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
            {/* Modal header */}
            <div style={{ padding: '18px 20px', borderBottom: '1px solid var(--slate)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.4rem', color: 'var(--cream)', margin: 0 }}>
                📋 Clone an Entry
              </h2>
              <button onClick={() => setShowCloneModal(false)} style={{ background: 'none', border: 'none', color: 'var(--gray)', fontSize: '1.4rem', cursor: 'pointer', lineHeight: 1 }}>✕</button>
            </div>

            {/* Modal body */}
            <div style={{ padding: '16px 20px', overflowY: 'auto', flex: 1 }}>
              <p style={{ color: 'var(--gray)', fontFamily: 'var(--font-condensed)', fontSize: '0.9rem', marginBottom: 16 }}>
                Select an entry to copy its picks, sleeper, and tiebreaker. You'll still need to give the new entry a name.
                {pastPlayIn && <span style={{ color: 'var(--orange)', display: 'block', marginTop: 6 }}>⏰ {playInRoundName} deadline has passed — play-in picks will not be copied.</span>}
              </p>

              {cloneLoading ? (
                <div style={{ textAlign: 'center', padding: '32px 0' }}><div className="spinner" /></div>
              ) : cloneBrackets.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--gray)', fontFamily: 'var(--font-condensed)' }}>
                  No existing entries to clone.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {cloneBrackets.map(b => (
                    <button
                      key={b.id}
                      onClick={() => handleCloneSelect(b)}
                      style={{
                        background: 'var(--navy-light)',
                        border: '1px solid var(--slate)',
                        borderRadius: 8,
                        padding: '12px 16px',
                        cursor: 'pointer',
                        textAlign: 'left',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        transition: 'border-color 0.15s',
                        width: '100%',
                      }}
                      onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--orange)'}
                      onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--slate)'}
                    >
                      <div>
                        <div style={{ fontFamily: 'var(--font-condensed)', fontWeight: 700, fontSize: '1rem', color: 'var(--cream)' }}>{b.name}</div>
                        <div style={{ fontSize: '0.78rem', color: 'var(--gray)', marginTop: 2 }}>
                          {b.sleeperTeamId
                            ? (b._sleeperName
                                ? `🌟 Sleeper: ${b._sleeperName}`
                                : '🌟 Sleeper: ⚠ Unknown (will need to be re-selected)')
                            : 'No sleeper set'}
                          {b.tiebreaker != null && b.tiebreaker !== '' ? `  ·  🎯 TB: ${b.tiebreaker}` : ''}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: 12 }}>
                        <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.6rem', color: 'var(--orange)', lineHeight: 1 }}>{b.score ?? 0}</div>
                        <div style={{ fontSize: '0.65rem', color: 'var(--gray)', letterSpacing: '1px' }}>PTS</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Modal footer */}
            <div style={{ padding: '14px 20px', borderTop: '1px solid var(--slate)', textAlign: 'right' }}>
              <button className="btn btn-secondary" onClick={() => setShowCloneModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Nav button ───────────────────────────────────────────────────────────────
function NavBtn({ label, sub, active, done, locked, onClick }) {
  return (
    <button onClick={onClick} style={{
      flex: 1, padding: '10px 6px', background: active ? 'rgba(255,107,0,0.15)' : 'none',
      border: 'none', borderBottom: active ? '3px solid var(--orange)' : '3px solid transparent',
      borderRight: '1px solid var(--slate)',
      color: locked ? 'var(--red)' : active ? 'var(--orange)' : done ? 'var(--green)' : 'var(--gray)',
      fontFamily: 'var(--font-condensed)', fontWeight: 700, fontSize: '0.8rem',
      letterSpacing: '0.5px', textTransform: 'uppercase',
      cursor: locked ? 'not-allowed' : 'pointer',
      transition: 'all 0.2s', opacity: locked ? 0.7 : 1,
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
    }}>
      <span>{label}</span>
      <span style={{ fontSize: '0.7rem', fontWeight: 400 }}>{done ? '✓ Done' : sub}</span>
    </button>
  );
}

// ── Bottom prev/next nav ─────────────────────────────────────────────────────
function BottomNav({ onPrev, onNext, prevLabel, nextLabel, onSave, saving, isNew }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 24, gap: 12 }}>
      {prevLabel
        ? <button className="btn btn-secondary" onClick={onPrev}>{prevLabel}</button>
        : <div />}
      {onSave
        ? <button className="btn btn-primary" onClick={onSave} disabled={saving}>
            {saving ? 'Saving...' : isNew ? '🏆 Submit Bracket' : '💾 Save Changes'}
          </button>
        : nextLabel
          ? <button className="btn btn-primary" onClick={onNext}>{nextLabel}</button>
          : <div />}
    </div>
  );
}

// ── Region bracket — horizontal columns ─────────────────────────────────────
function RegionBracket({ region, gamesById, picks, allPicks, teamsById, sleeperTeamId, onPick, pickResult, onPrev, onNext, prevLabel, nextLabel, overallSeedByRegion, feeder, readOnly, roundPoints = DEFAULT_ROUND_POINTS }) {
  const struct = REGION_STRUCTURE[region];

  // Figure out which rounds to show based on what's been picked
  // Always show R64. Show R32 once at least 2 R64 games are picked, etc.
  function roundVisible(round) {
    if (round === 'R64') return true;
    const prevRound = { R32: 'R64', R16: 'R32', R8: 'R16' }[round];
    const prevGames = struct[prevRound] || [];
    const pickedCount = prevGames.filter(gid => picks[gid]).length;
    // Show next round column once at least half of prev round is picked
    return pickedCount >= Math.ceil(prevGames.length / 2);
  }

  const visibleRounds = REGION_ROUNDS.filter(roundVisible);

  // Points per round
  const roundPts = { R64: roundPoints.R64, R32: roundPoints.R32, R16: roundPoints.R16, R8: roundPoints.R8 };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 16, marginBottom: 16 }}>
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '2.2rem', color: 'var(--cream)', letterSpacing: '3px' }}>
          {region.toUpperCase()} <span style={{ color: 'var(--orange)' }}>REGION</span>
        </h2>
        {overallSeedByRegion && overallSeedByRegion[region] && (
          <span style={{
            fontFamily: 'var(--font-condensed)', fontSize: '0.85rem', fontWeight: 700,
            color: 'var(--gold)', letterSpacing: '1px',
            background: 'rgba(241,196,15,0.1)', border: '1px solid var(--gold)',
            borderRadius: 6, padding: '2px 10px',
          }}>
            #{overallSeedByRegion[region]} OVERALL SEED
          </span>
        )}
      </div>

      <div style={{ display: 'flex', gap: 0, alignItems: 'flex-start', overflowX: 'auto', paddingBottom: 12 }}>
        {visibleRounds.map((round, colIdx) => {
          const gameIds = struct[round] || [];
          const pts = roundPts[round];
          const colPicked = gameIds.filter(gid => picks[gid]).length;
          const colDone = colPicked === gameIds.length;

          return (
            <div key={round} style={{ flex: '0 0 auto', width: 200, marginRight: 12 }}>
              {/* Column header */}
              <div style={{
                background: colDone ? 'rgba(39,174,96,0.15)' : 'rgba(255,107,0,0.1)',
                border: `1px solid ${colDone ? 'var(--green)' : 'var(--slate)'}`,
                borderRadius: '8px 8px 0 0',
                padding: '8px',
                textAlign: 'center',
                marginBottom: 0,
              }}>
                <div style={{ fontFamily: 'var(--font-condensed)', fontWeight: 700, fontSize: '0.85rem', letterSpacing: '1px', color: colDone ? 'var(--green)' : 'var(--orange)', textTransform: 'uppercase' }}>
                  {round === 'R64' ? 'Round of 64' : round === 'R32' ? 'Round of 32' : round === 'R16' ? 'Sweet 16' : 'Elite Eight'}
                </div>
                <div style={{ fontSize: '0.7rem', color: 'var(--gray)', marginTop: 2 }}>
                  {colDone ? '✓ Complete' : `${colPicked}/${gameIds.length} picked`} · {pts} pt{pts>1?'s':''}
                </div>

              </div>

              {/* Games stacked vertically with spacing to align with bracket */}
              <div style={{
                border: '1px solid var(--slate)',
                borderTop: 'none',
                borderRadius: '0 0 8px 8px',
                padding: '10px 8px',
                paddingTop: colIdx === 0 ? 10 : colIdx === 1 ? 44 : colIdx === 2 ? 120 : 237,
                display: 'flex',
                flexDirection: 'column',
                gap: colIdx === 0 ? 6 : colIdx === 1 ? 82 : colIdx === 2 ? 234 : 0,
                background: 'var(--navy-mid)',
              }}>
                {gameIds.map(gid => {
                  const [topTeam, bottomTeam] = getSlots(gid, gamesById, allPicks, teamsById, feeder);
                  const game = gamesById[gid];
                  const currentPick = picks[gid];
                  return (
                    <CompactGameCard
                      key={gid}
                      gameId={gid}
                      game={game}
                      topTeam={topTeam}
                      bottomTeam={bottomTeam}
                      currentPick={currentPick}
                      sleeperTeamId={sleeperTeamId}
                      onPick={tid => onPick(gid, tid)}
                      pickResult={pickResult}
                      readOnly={readOnly}
                    />
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* Regional champion display */}
        {regionComplete(region, picks, struct) && (
          <div style={{ flex: '0 0 auto', width: 180, marginLeft: 4, alignSelf: 'center' }}>
            <div style={{ background: 'rgba(241,196,15,0.1)', border: '2px solid var(--gold)', borderRadius: 10, padding: 16, textAlign: 'center' }}>
              <div style={{ fontSize: '1.5rem', marginBottom: 6 }}>🏆</div>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem', color: 'var(--gold)', letterSpacing: '1px' }}>
                {region.toUpperCase()} CHAMP
              </div>
              {(() => {
                const champ = resolveTeam('r8-' + region[0].toLowerCase(), gamesById, allPicks, teamsById);
                return champ ? (
                  <div style={{ marginTop: 8 }}>
                    <div style={{ fontFamily: 'var(--font-condensed)', fontSize: '0.8rem', color: 'var(--gray)' }}>({champ.seed})</div>
                    <div style={{ fontFamily: 'var(--font-condensed)', fontWeight: 700, fontSize: '1rem', color: 'var(--cream)' }}>{champ.name}</div>
                  </div>
                ) : null;
              })()}
            </div>
          </div>
        )}
      </div>

      <BottomNav onPrev={onPrev} onNext={onNext} prevLabel={prevLabel} nextLabel={nextLabel} />
    </div>
  );
}

function regionComplete(region, picks, struct) {
  return struct && REGION_ROUNDS.every(round =>
    (struct[round] || []).every(gid => !!picks[gid])
  );
}



// ── Finals view ──────────────────────────────────────────────────────────────
function FinalsView({ gamesById, picks, allPicks, teamsById, sleeperTeamId, onPick, pickResult, onPrev, onSave, saving, isNew, prevRegionLabel, wiring, feeder, readOnly, roundPoints = DEFAULT_ROUND_POINTS }) {
  const semifinal1 = gamesById['r4-1']; // South vs West
  const semifinal2 = gamesById['r4-2']; // East vs Midwest
  const championship = gamesById['final-1'];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 16, marginBottom: 20 }}>
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '2.2rem', color: 'var(--cream)', letterSpacing: '3px' }}>
          FINAL <span style={{ color: 'var(--orange)' }}>FOUR</span>
        </h2>
        <span className="badge badge-orange">{roundPoints.R4} pts per semifinal</span>
        <span className="badge badge-gold">{roundPoints.Final} pts for Champion</span>
      </div>

      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        {/* Semifinal 1 */}
        <div style={{ flex: '0 0 auto' }}>
          <div style={{ fontFamily: 'var(--font-condensed)', fontSize: '0.8rem', color: 'var(--gray)', letterSpacing: '1px', textTransform: 'uppercase', marginBottom: 8 }}>
            Semifinal 1 · {wiring?.finalFour?.semi1?.join(' vs ') || 'South vs West'}
          </div>
          {semifinal1 && (() => {
            const [t, b] = getSlots('r4-1', gamesById, allPicks, teamsById, feeder);
            return <CompactGameCard gameId="r4-1" game={semifinal1} topTeam={t} bottomTeam={b}
              currentPick={picks['r4-1']} sleeperTeamId={sleeperTeamId}
              onPick={tid => onPick('r4-1', tid)} pickResult={pickResult} wide readOnly={readOnly} />;
          })()}
        </div>

        {/* Arrow */}
        <div style={{ alignSelf: 'center', color: 'var(--orange)', fontSize: '1.5rem', paddingTop: 24 }}>→</div>

        {/* Championship */}
        <div style={{ flex: '0 0 auto' }}>
          <div style={{ fontFamily: 'var(--font-condensed)', fontSize: '0.8rem', color: 'var(--gold)', letterSpacing: '1px', textTransform: 'uppercase', marginBottom: 8 }}>
            🏆 National Championship
          </div>
          {championship && (() => {
            const [t, b] = getSlots('final-1', gamesById, allPicks, teamsById, feeder);
            return <CompactGameCard gameId="final-1" game={championship} topTeam={t} bottomTeam={b}
              currentPick={picks['final-1']} sleeperTeamId={sleeperTeamId}
              onPick={tid => onPick('final-1', tid)} pickResult={pickResult} wide championship readOnly={readOnly} />;
          })()}
        </div>

        {/* Arrow */}
        <div style={{ alignSelf: 'center', color: 'var(--orange)', fontSize: '1.5rem', paddingTop: 24 }}>←</div>

        {/* Semifinal 2 */}
        <div style={{ flex: '0 0 auto' }}>
          <div style={{ fontFamily: 'var(--font-condensed)', fontSize: '0.8rem', color: 'var(--gray)', letterSpacing: '1px', textTransform: 'uppercase', marginBottom: 8 }}>
            Semifinal 2 · {wiring?.finalFour?.semi2?.join(' vs ') || 'East vs Midwest'}
          </div>
          {semifinal2 && (() => {
            const [t, b] = getSlots('r4-2', gamesById, allPicks, teamsById, feeder);
            return <CompactGameCard gameId="r4-2" game={semifinal2} topTeam={t} bottomTeam={b}
              currentPick={picks['r4-2']} sleeperTeamId={sleeperTeamId}
              onPick={tid => onPick('r4-2', tid)} pickResult={pickResult} wide readOnly={readOnly} />;
          })()}
        </div>
      </div>

      <BottomNav onPrev={onPrev} prevLabel={`← ${prevRegionLabel || 'West'} Region`} onSave={readOnly ? null : onSave} saving={saving} isNew={isNew} />
    </div>
  );
}

// ── Compact game card (used in columns) ──────────────────────────────────────
// Checks if a team is the current pick, handling play-in pending slots
// currentPick can be a teamId OR a playInGameId
// team.id can be a real teamId OR 'playin-pending-<gameId>'
function isPickedTeam(currentPick, team) {
  if (!currentPick || !team) return false;
  if (team.isTbd && !team.isPlayInPending) return false;
  if (currentPick === team.id) return true;
  // currentPick is a playInGameId; team.id is 'playin-pending-<gameId>'
  if (team.isPlayInPending && team.playInGameId && currentPick === team.playInGameId) return true;
  // currentPick is 'playin-pending-<gameId>'; team.id is the playInGameId
  if (currentPick.startsWith?.('playin-pending-') && currentPick === 'playin-pending-' + team.id) return true;
  return false;
}

function CompactGameCard({ gameId, game, topTeam, bottomTeam, currentPick, sleeperTeamId, onPick, pickResult, wide, championship, readOnly }) {
  const isLocked = readOnly || game?.status === 'final';
  const winner = game?.winner;
  const width = championship ? 220 : wide ? 200 : '100%';

  return (
    <div style={{
      width,
      border: championship ? '2px solid var(--gold)' : '1px solid var(--slate)',
      borderRadius: 8,
      overflow: 'hidden',
      background: 'var(--navy-light)',
      boxShadow: championship ? '0 0 16px rgba(241,196,15,0.2)' : 'none',
    }}>
      <TeamSlot
        team={topTeam}
        isPicked={isPickedTeam(currentPick, topTeam)}
        isWinner={!topTeam?.isTbd && winner === topTeam?.id}
        isLoser={!topTeam?.isTbd && !!(winner && winner !== topTeam?.id)}
        isSleeper={!topTeam?.isTbd && sleeperTeamId === topTeam?.id}
        result={null}
        onClick={() => !isLocked && topTeam && (topTeam.isPlayInPending || !topTeam.isTbd) && onPick(topTeam.id)}
        score={game?.status !== 'scheduled' ? topTeam?.score : undefined}
        championship={championship}
      />
      <div style={{ height: 1, background: 'var(--slate)' }} />
      <TeamSlot
        team={bottomTeam}
        isPicked={isPickedTeam(currentPick, bottomTeam)}
        isWinner={!bottomTeam?.isTbd && winner === bottomTeam?.id}
        isLoser={!bottomTeam?.isTbd && !!(winner && winner !== bottomTeam?.id)}
        isSleeper={!bottomTeam?.isTbd && sleeperTeamId === bottomTeam?.id}
        result={null}
        onClick={() => !isLocked && bottomTeam && (bottomTeam.isPlayInPending || !bottomTeam.isTbd) && onPick(bottomTeam.id)}
        score={game?.status !== 'scheduled' ? bottomTeam?.score : undefined}
        championship={championship}
      />
    </div>
  );
}

// ── Mini game card (used for play-in) ────────────────────────────────────────
function MiniGameCard({ game, topTeam, bottomTeam, currentPick, sleeperTeamId, onPick, pickResult }) {
  const isLive = game?.status === 'live';
  const isFinal = game?.status === 'final';
  const isLocked = isFinal || isLive;
  const winner = game?.winner;

  // For live games, dim both teams to indicate locked
  const topLosing = isLive;
  const botLosing = isLive;

  return (
    <div style={{ border: `1px solid ${isLive ? 'var(--green)' : 'var(--slate)'}`, borderRadius: 10, overflow: 'hidden', background: 'var(--navy-mid)' }}>
      <div style={{ padding: '6px 10px', background: 'var(--navy-light)', display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid var(--slate)' }}>
        <span style={{ fontSize: '0.7rem', color: 'var(--gray)', fontFamily: 'var(--font-condensed)', letterSpacing: '1px', textTransform: 'uppercase' }}>{game.shortName}</span>
        <span>
          {isFinal && <span className="badge badge-gray" style={{ fontSize: '0.65rem' }}>FINAL</span>}
          {isLive && (
            <span className="badge badge-green" style={{ fontSize: '0.65rem' }}>
              ● {game.isHalftime ? 'HALFTIME' : game.period ? `${game.period === 1 ? '1st' : '2nd'} ${game.displayClock || 'LIVE'}` : 'LIVE'}
            </span>
          )}
          {game.status === 'scheduled' && <span className="badge badge-orange" style={{ fontSize: '0.65rem' }}>TBD</span>}
        </span>
      </div>
      <TeamSlot team={topTeam} isPicked={currentPick === topTeam?.id} isWinner={winner === topTeam?.id}
        isLoser={!!(winner && winner !== topTeam?.id) || topLosing} isSleeper={sleeperTeamId === topTeam?.id}
        result={currentPick === topTeam?.id ? pickResult(game.id, topTeam?.id) : null}
        onClick={() => !isLocked && topTeam && onPick(topTeam.id)}
        score={isLive || isFinal ? topTeam?.score : undefined} />
      <div style={{ height: 1, background: 'var(--slate)' }} />
      <TeamSlot team={bottomTeam} isPicked={currentPick === bottomTeam?.id} isWinner={winner === bottomTeam?.id}
        isLoser={!!(winner && winner !== bottomTeam?.id) || botLosing} isSleeper={sleeperTeamId === bottomTeam?.id}
        result={currentPick === bottomTeam?.id ? pickResult(game.id, bottomTeam?.id) : null}
        onClick={() => !isLocked && bottomTeam && onPick(bottomTeam.id)}
        score={isLive || isFinal ? bottomTeam?.score : undefined} />
    </div>
  );
}

// ── Team slot ────────────────────────────────────────────────────────────────
function TeamSlot({ team, isPicked, isWinner, isLoser, isSleeper, result, onClick, score, championship, overallLabel }) {
  if (!team) return null;
  const isTbd = team.isTbd && !team.isPlayInPending; // pending play-in slots ARE clickable

  const isElimPick = result === 'eliminated';
  const bg = result === 'correct' ? 'rgba(39,174,96,0.12)'
    : (result === 'incorrect' || isElimPick) ? 'rgba(231,76,60,0.08)'
    : isPicked && !isWinner && !isLoser ? 'rgba(255,107,0,0.12)'
    : isWinner ? 'rgba(39,174,96,0.12)'
    : 'transparent';
  const textColor = result === 'correct' ? 'var(--green)'
    : (result === 'incorrect' || isElimPick) ? 'var(--red)'
    : isWinner ? 'var(--green)'
    : isLoser || isTbd ? 'var(--gray)'
    : isPicked ? 'var(--orange)'
    : 'var(--cream)';
  const borderLeft = result === 'correct' ? '3px solid var(--green)'
    : (result === 'incorrect' || isElimPick) ? '3px solid var(--red)'
    : '3px solid transparent';

  return (
    <div onClick={isTbd || isLoser ? undefined : onClick} style={{
      display: 'flex', alignItems: 'center', gap: 6,
      padding: championship ? '10px 12px' : '7px 10px',
      background: bg,
      borderLeft,
      cursor: isTbd || isLoser || isWinner ? 'default' : 'pointer',
      transition: 'background 0.15s',
      opacity: isLoser ? 0.45 : 1,
    }}
    onMouseEnter={e => { if (!isTbd && !isLoser && !isWinner) e.currentTarget.style.background = 'rgba(255,107,0,0.08)'; }}
    onMouseLeave={e => { e.currentTarget.style.background = bg; }}
    >
      <span style={{ fontFamily: 'var(--font-condensed)', fontSize: '0.7rem', color: 'var(--gray)', minWidth: 14, textAlign: 'center' }}>
        {isTbd ? '?' : team.seed}
      </span>
      <span style={{ fontFamily: 'var(--font-condensed)', fontWeight: 600, fontSize: championship ? '1rem' : '0.82rem', color: textColor, flex: 1, fontStyle: isTbd ? 'italic' : 'normal', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textDecoration: isElimPick ? 'line-through' : 'none' }}>
        {isTbd ? 'TBD' : team.name}
      </span>
      {isSleeper && <span style={{ fontSize: '0.6rem', color: 'var(--gold)', background: 'rgba(241,196,15,0.15)', padding: '1px 4px', borderRadius: 3, border: '1px solid var(--gold)' }}>★</span>}
      {result === 'correct' && <span style={{ fontSize: '0.75rem', color: 'var(--green)', marginLeft: 'auto' }}>✓</span>}
      {result === 'incorrect' && <span style={{ fontSize: '0.75rem', color: 'var(--red)', marginLeft: 'auto' }}>✗</span>}
      {!result && isPicked && !isWinner && !isLoser && <span style={{ fontSize: '0.65rem', color: 'var(--orange)' }}>✓</span>}
      {score !== undefined && !isTbd && (
        <span style={{ fontFamily: 'var(--font-display)', fontSize: '0.9rem', color: textColor, marginLeft: 'auto', minWidth: 24, textAlign: 'right' }}>{score}</span>
      )}

    </div>
  );
}
